import { useMemo } from "react";
import { useSelector } from "react-redux";
import { routeConfig, getRoutesForRole, flattenRoutes } from "@/routes/config";
import { RouteConfig, FlatRoute } from "@/types/navigation.types";
import { UserRole } from "@shared/constants/api";

interface UseRouteAuthorizationResult {
  authorizedRoutes: RouteConfig[];
  flatRoutes: FlatRoute[];
  userRole: UserRole;
  hasAccess: (path: string) => boolean;
}

export const useRouteAuthorization = (): UseRouteAuthorizationResult => {
  const userRole = useSelector((state: any) => state.auth.user?.role);

  const authorizedRoutes = useMemo(() => {
    if (!userRole) return [];
    return getRoutesForRole(routeConfig, userRole);
  }, [userRole]);

  const flatRoutes = useMemo(() => {
    return flattenRoutes(authorizedRoutes);
  }, [authorizedRoutes]);

  const hasAccess = useMemo(() => {
    const pathSet = new Set(flatRoutes.map((r) => r.path));
    return (path: string): boolean => {
      if (pathSet.has(path)) return true;
      return flatRoutes.some((route) => {
        const pattern = route.path.replace(/:[^/]+/g, "[^/]+");
        const regex = new RegExp(`^${pattern}$`);
        return regex.test(path);
      });
    };
  }, [flatRoutes]);

  return {
    authorizedRoutes,
    flatRoutes,
    userRole,
    hasAccess,
  };
};
